import { useSelector } from "react-redux"
import { useTranslation } from "react-i18next"

import wsClient from "src/wsClient"
import ModalService from "src/modals"
import { selectLobby } from "src/store/lobby"
import { selectUserId } from "src/store/user"
import { selectGameConfig } from "src/store/common"
import { selectOwnedCosmetics } from "src/store/economy"
import { seatIconFor } from "src/utils/cosmetics"
import { unitIcons } from "src/assets/images"

import Modal from "src/components/modals/Modal"
import "./LookPickerModal.css"

/**
 * Which look this lobby seat plays in (owner, 2026-09-29: "instead of the class image, put the
 * icon there. And when pressing, we can change it in a modal").
 *
 * Opened by pressing the icon on your own seat. It lists what you may wear on that side — the free
 * colours and whatever the shop sold you — and nothing you would be refused: the shelf of locked
 * looks belongs to the shop, which the footer opens. A look somebody on your side already wears is
 * shown struck out with their name, because "taken" is why it cannot be picked.
 *
 * Everything is read off the store rather than handed in as props, so the sheet follows the room
 * while it is open: a teammate who takes a colour greys it out here the moment the lobby says so.
 *
 * Choosing sends `change_unit_icon` — the seat's look, which is what the game copies onto the
 * `Player` and the map draws — and the server answers the whole room, so every screen updates. The
 * server is the gate on side, ownership and clashes (`change_unit_icon_in_lobby`); this sheet only
 * keeps a player from finding that out by being refused.
 */
const LookPickerModal = () => {
  const { t } = useTranslation()
  const lobby = useSelector(selectLobby)
  const userId = useSelector(selectUserId)
  const gameConfig = useSelector(selectGameConfig)
  const ownedCosmetics = useSelector(selectOwnedCosmetics)

  const seat = lobby?.lobbyUsers?.find((lobbyUser) => lobbyUser.user.id === userId)
  const team = seat?.team
  const catalogue = gameConfig?.economy?.cosmetics || []
  const labels = Object.fromEntries(catalogue.map((cosmetic) => [cosmetic.id, cosmetic.label]))
  const free = catalogue.filter((cosmetic) => cosmetic.free).map((cosmetic) => cosmetic.id)
  const owned = new Set([...free, ...ownedCosmetics, team ? `${team}_default` : null].filter(Boolean))
  const icons = (gameConfig?.economy?.unit_icons_by_team?.[team] || []).filter((icon) => owned.has(icon))
  const wearing = seat ? seatIconFor(seat) : null
  const canChange = Boolean(team) && !seat?.ready

  const choose = (icon) => {
    if (icon !== wearing) wsClient.send("lobby", { action: "change_unit_icon", unit_icon: icon })
    ModalService.hide()
  }

  const openShop = () => {
    ModalService.hide()
    ModalService.show("ShopModal", { initialTab: "looks" })
  }

  return (
    <Modal width="520px">
      <div className="look-picker">
        <h2>{t("lobby.lookPickerTitle")}</h2>
        <p className="look-picker-lead">{t("lobby.lookPickerLead")}</p>

        {canChange ? (
          <div className="look-picker-grid" role="radiogroup" aria-label={t("lobby.yourColour")}>
            {icons.map((icon) => {
              const takenBy = lobby.lobbyUsers.find(
                (other) => other.team === team && other.user.id !== userId && other.unitIcon === icon
              )
              const selected = icon === wearing
              const label = labels[icon] || icon
              return (
                <button
                  key={icon}
                  type="button"
                  role="radio"
                  aria-checked={selected}
                  aria-label={label}
                  className={`look-picker-option${selected ? " selected" : ""}${takenBy ? " taken" : ""}`}
                  disabled={Boolean(takenBy)}
                  title={takenBy ? t("lobby.colourTaken", { name: takenBy.user.username }) : label}
                  onClick={() => choose(icon)}
                >
                  <img src={unitIcons[icon]} alt="" />
                  {selected ? <span className="look-picker-tag">{t("lobby.lookWearing")}</span> : null}
                  {takenBy ? <span className="look-picker-taken">{takenBy.user.username}</span> : null}
                </button>
              )
            })}
          </div>
        ) : (
          <p className="look-picker-lead">{t("lobby.lookPickerLocked")}</p>
        )}

        <div className="look-picker-footer">
          <button type="button" className="quiet" onClick={openShop}>
            {t("lobby.lookPickerShop")}
          </button>
        </div>
      </div>
    </Modal>
  )
}

export default LookPickerModal
